import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactPlayer from "react-player";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NdaDialog from "@/components/NdaDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Lock, MapPin, TrendingUp, Users, Calendar, Euro, Building2, ArrowLeft, Play, Mail, Phone, User, ShieldCheck, CheckCircle2, Landmark, Wallet } from "lucide-react";
import { forSaleText } from "@/lib/proacText";

const fmtPct = (v) => (v == null ? "Sur dossier" : `${v}%`);
const fmtMoney = (n) => (n == null ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n));

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [ndaOpen, setNdaOpen] = useState(false);

  const loadBusiness = () => {
    setLoading(true);
    api.get(`/businesses/${id}`).then((r) => setB(r.data)).catch(() => setB(null)).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBusiness();
  }, [id, user]);

  const sendInquiry = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/businesses/${id}/inquiry`, { message: msg });
      toast.success("Votre demande a bien été transmise au conseiller. Un email de confirmation vous a été envoyé.");
      setMsg("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#1B2A4A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!b) return <div className="min-h-screen flex flex-col"><Header /><div className="flex-1 flex items-center justify-center text-gray-500">Entreprise introuvable. <Link to="/catalogue" className="text-[#E0C173] ml-2">Retour au catalogue</Link></div></div>;

  const locked = b.locked;

  return (
    <div className="bg-[#FDFDFC] min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative h-[44vh] min-h-[320px]">
        <img src={b.hero_image} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D1629] via-[#1B2A4A]/50 to-[#1B2A4A]/20" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 h-full flex flex-col justify-end pb-10">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="absolute top-6 left-6 sm:left-8 text-white/80 hover:text-white flex items-center gap-1 text-sm"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <div className="flex flex-wrap items-center gap-2">
            <span className="bg-[#E0C173] text-white text-xs uppercase tracking-wider px-3 py-1 w-fit">{b.sector}</span>
            {b.reference && <span data-testid="detail-reference" className="border border-white/40 text-white text-xs uppercase tracking-wider px-3 py-1">Réf. {b.reference}</span>}
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight max-w-3xl">{b.title}</h1>
          <div className="mt-3 flex items-center gap-5 text-white/80 text-sm">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#E0C173]" strokeWidth={1.5} /> {[b.city, b.region].filter(Boolean).join(", ")}</span>
            {b.profitability_rate != null && <span className="flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-[#E0C173]" strokeWidth={1.5} /> Rentabilité {b.profitability_rate}%</span>}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 sm:px-8 py-12">
        {/* Teaser figures */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mb-10">
          {[
            { label: "Secteur", value: b.sector, icon: Building2 },
            { label: "Région", value: b.region, icon: MapPin },
            { label: "Rentabilité", value: fmtPct(b.profitability_rate), icon: TrendingUp },
            { label: "Prix", value: b.asking_price_range, icon: Euro },
          ].map((m) => (
            <div key={m.label} className="bg-white p-6">
              <m.icon className="w-5 h-5 text-[#E0C173] mb-3" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-widest text-[#475569]">{m.label}</div>
              <div className="mt-1 font-display text-lg text-[#1B2A4A]">{m.value}</div>
            </div>
          ))}
        </div>

        <p className="text-[#475569] leading-relaxed text-lg max-w-3xl">{forSaleText(b.title, b.short_description, b.status === "sold")}</p>
        {b.highlights?.length > 0 && (
          <ul data-testid="detail-highlights" className="mt-6 grid sm:grid-cols-2 gap-3 max-w-3xl">
            {b.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-[#1B2A4A]"><CheckCircle2 className="w-5 h-5 text-[#E0C173] shrink-0 mt-0.5" strokeWidth={1.5} /> {h}</li>
            ))}
          </ul>
        )}

        {locked ? (
          /* Gated section */
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-12 relative border border-gray-200 bg-white overflow-hidden">
            <div className="p-8 sm:p-12 blur-sm select-none pointer-events-none">
              <div className="aspect-video bg-gray-100 mb-8 flex items-center justify-center"><Play className="w-16 h-16 text-gray-300" /></div>
              <div className="grid grid-cols-3 gap-6">
                {["Chiffre d'affaires", "EBITDA", "Effectif"].map((l) => (
                  <div key={l}><div className="text-xs uppercase text-gray-400">{l}</div><div className="font-display text-2xl text-[#1B2A4A]">•••••</div></div>
                ))}
              </div>
            </div>
            <div className="absolute inset-0 backdrop-blur-md bg-white/70 flex flex-col items-center justify-center p-8 text-center">
              {b.nda_required ? (
                <>
                  <div className="w-14 h-14 bg-[#1B2A4A] flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} /></div>
                  <h3 className="mt-6 font-display text-2xl text-[#1B2A4A]">Signez l'accord de confidentialité</h3>
                  <p className="mt-3 text-[#475569] max-w-md">Pour accéder à la vidéo, aux chiffres détaillés (CA, EBITDA, prix de cession) et au contact du conseiller, vous devez signer l'accord de confidentialité. Cette signature est unique et débloque tous les dossiers.</p>
                  <div className="mt-7 flex gap-3">
                    <Button data-testid="gate-nda-btn" onClick={() => setNdaOpen(true)} className="rounded-full bg-[#1B2A4A] hover:bg-[#0D1629] text-white px-8 py-5"><ShieldCheck className="w-4 h-4 mr-2" /> Signer l'accord de confidentialité</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 bg-[#1B2A4A] flex items-center justify-center"><Lock className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} /></div>
                  <h3 className="mt-6 font-display text-2xl text-[#1B2A4A]">Dossier complet réservé aux acheteurs</h3>
                  <p className="mt-3 text-[#475569] max-w-md">Créez votre profil acheteur pour accéder à la vidéo de présentation, aux chiffres détaillés (CA, EBITDA, prix de cession) et au contact du conseiller.</p>
                  <div className="mt-7 flex gap-3">
                    <Button data-testid="gate-register-btn" onClick={() => navigate("/inscription")} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-white px-8 py-5">Devenir acheteur</Button>
                    <Button data-testid="gate-login-btn" onClick={() => navigate("/connexion")} variant="outline" className="rounded-full border-[#1B2A4A] text-[#1B2A4A] px-8 py-5">J'ai déjà un compte</Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        ) : (
          /* Full private view */
          <div className="mt-12 space-y-12">
            {b.video_url && (
              <div>
                <h2 className="font-display text-2xl text-[#1B2A4A] mb-5 flex items-center gap-2"><Play className="w-5 h-5 text-[#E0C173]" /> Vidéo de présentation</h2>
                <div className="aspect-video bg-black border border-gray-200">
                  <ReactPlayer src={b.video_url} width="100%" height="100%" controls />
                </div>
              </div>
            )}

            <div>
              <h2 className="font-display text-2xl text-[#1B2A4A] mb-5">Chiffres clés</h2>
              <div data-testid="detail-key-figures" className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: "Chiffre d'affaires", value: b.revenue != null && fmtMoney(b.revenue), icon: Euro },
                  { label: "EBE / EBITDA", value: b.ebitda != null && fmtMoney(b.ebitda), icon: TrendingUp },
                  { label: "Résultat net", value: b.net_result != null && fmtMoney(b.net_result), icon: Wallet },
                  { label: "Prix de cession", value: b.asking_price != null && fmtMoney(b.asking_price), icon: Euro },
                  { label: "Foncier (en option)", value: b.real_estate_price != null && fmtMoney(b.real_estate_price), icon: Landmark },
                  { label: "Effectif", value: b.employees && `${b.employees} personnes`, icon: Users },
                  { label: "Création", value: b.year_founded, icon: Calendar },
                  { label: "Rentabilité", value: b.profitability_rate != null && `${b.profitability_rate}%`, icon: TrendingUp },
                ].filter((m) => m.value).map((m) => (
                  <div key={m.label} className="bg-white border border-gray-200 p-6">
                    <m.icon className="w-5 h-5 text-[#E0C173] mb-3" strokeWidth={1.5} />
                    <div className="text-xs uppercase tracking-widest text-[#475569]">{m.label}</div>
                    <div className="mt-1 font-display text-xl text-[#1B2A4A]">{m.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {b.full_description && (
              <div>
                <h2 className="font-display text-2xl text-[#1B2A4A] mb-4">Présentation détaillée</h2>
                <p className="text-[#475569] leading-relaxed whitespace-pre-line">{forSaleText(b.title, b.full_description, b.status === "sold")}</p>
              </div>
            )}

            {b.gallery?.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4">
                {b.gallery.map((g, i) => <img key={i} src={g} alt="" className="w-full h-64 object-cover border border-gray-200" />)}
              </div>
            )}

            {/* Advisor + contact */}
            <div className="grid lg:grid-cols-2 gap-8 bg-[#1B2A4A] text-white p-8 sm:p-10">
              <div>
                <span className="text-[#E0C173] uppercase tracking-widest text-xs">Votre conseiller</span>
                <h3 className="mt-3 font-display text-2xl flex items-center gap-2"><User className="w-5 h-5 text-[#E0C173]" /> {b.advisor_name}</h3>
                <div className="mt-5 space-y-3 text-sm text-white/80">
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#E0C173]" /> {b.advisor_email}</p>
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#E0C173]" /> {b.advisor_phone}</p>
                </div>
              </div>
              <div>
                <span className="text-[#E0C173] uppercase tracking-widest text-xs">Demander plus d'informations</span>
                <Textarea data-testid="inquiry-message" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Bonjour, je suis intéressé(e) par cette entreprise…" className="mt-3 rounded-none bg-white/10 border-white/20 text-white placeholder:text-white/40 min-h-[110px]" />
                <Button data-testid="inquiry-submit" onClick={sendInquiry} disabled={sending} className="mt-4 rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-white w-full py-5">{sending ? "Envoi…" : "Envoyer ma demande"}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
      <NdaDialog open={ndaOpen} onOpenChange={setNdaOpen} onSigned={loadBusiness} />
    </div>
  );
}
